/* eslint-disable no-param-reassign */
import { fieldType, fieldDefaultValue } from './Constant';

/**
 * Shared Util for Prepper and Approver
 */
class FieldUtil {
  static arrayUnique(theArray) {
    return theArray.filter((value, index, uArray) => uArray.indexOf(value) === index);
  }

  static getPdfObject(url) {
    return {
      url,
      httpHeaders: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Headers': 'Accept-Ranges',
      },
    };
  }

  static getOffset(elem) {
    // Return zeros for disconnected and hidden (display: none) elements (gh-2310)
    // Support: IE <=11 only
    // Running getBoundingClientRect on a
    // disconnected node in IE throws an error
    if (!elem.getClientRects().length) {
      return { top: 0, left: 0 };
    }

    // Get document-relative position by adding viewport scroll to viewport-relative gBCR
    const rect = elem.getBoundingClientRect();
    const win = elem.ownerDocument.defaultView;
    return {
      top: rect.top + win.pageYOffset,
      left: rect.left + win.pageXOffset,
    };
  }

  static getHoveringFieldCoordinate(event, elem, hoveringField) {
    const parentOffset = this.getOffset(elem);
    const relX = event.pageX - parentOffset.left;
    const relY = event.pageY - parentOffset.top;
    let top = parseFloat(relY - parseFloat(fieldDefaultValue.defHeight / 2));
    let left = parseFloat(relX - parseFloat(fieldDefaultValue.defWidth / 2));
    const type = hoveringField.type;
    if (type === fieldType.SIGNATURE) {
      top = parseFloat(relY - parseFloat(fieldDefaultValue.defSigHeight / 2));
      left = parseFloat(relX - parseFloat(fieldDefaultValue.defSigWidth / 2));
    } else if (type === fieldType.CHECK_BOX) {
      top = parseFloat(relY - parseFloat(fieldDefaultValue.defCheckHeight / 2));
      left = parseFloat(relX - parseFloat(fieldDefaultValue.defCheckWidth / 2));
    } else if (type === fieldType.FREE_TEXT) {
      top = parseFloat(relY - parseFloat(fieldDefaultValue.defTextHeight / 2));
      left = parseFloat(relX - parseFloat(fieldDefaultValue.defTextWidth / 2));
    }
    return { top, left };
  }

  static getDefaultFieldDimension(type) {
    let minHeight = fieldDefaultValue.defHeight;
    let minWidth = fieldDefaultValue.defWidth;
    if (type === fieldType.SIGNATURE) {
      minHeight = fieldDefaultValue.defSigHeight;
      minWidth = fieldDefaultValue.defSigWidth;
    } else if (type === fieldType.CHECK_BOX) {
      minHeight = fieldDefaultValue.defCheckHeight;
      minWidth = fieldDefaultValue.defCheckWidth;
    } else if (type === fieldType.FREE_TEXT) {
      minHeight = fieldDefaultValue.defTextHeight;
      minWidth = fieldDefaultValue.defTextWidth;
    }
    return { minWidth, minHeight };
  }

  static isUndefined(str) {
    return str === null || str === undefined;
  }

  static isStringEmpty(str) {
    return str === null || str === undefined || str === '';
  }

  static sanitizePrepperParticipant(contactList) {
    const participants = [];
    for (const contact of contactList) {
      const participant = {
        emailAddress: contact.emailAddress,
        firstName: contact.firstName,
        lastName: contact.lastName,
      };
      participants.push(participant);
    }
    return participants;
  }

  static findParticipantIndex(email, participants) {
    const arrayIndex = participants.findIndex(participant => participant.emailAddress === email);
    if (arrayIndex < 0) return 0;
    return arrayIndex;
  }

  /**
   * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
   *
   * @param {String} text The text to be rendered.
   * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
   *
   * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
   */
  static getTextWidth(text, font) {
    // re-use canvas object for better performance
    const canvas = this.getTextWidth.canvas || (this.getTextWidth.canvas = document.createElement('canvas'));
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    console.log(metrics);
    return metrics.width;
  }

  static scaleFontSizeToOriginal(fieldAreaWidth, fieldAreaOriginalWidth, fontSize) {
    const originalFontSize = (fieldAreaOriginalWidth / fieldAreaWidth) * fontSize;
    return Number.isNaN(originalFontSize) ? fontSize : originalFontSize;
  }

  static scaleFieldWidthToOriginal(fieldAreaWidth, fieldAreaOriginalWidth, fieldWidth) {
    const originalFieldWidth = (fieldAreaOriginalWidth / fieldAreaWidth) * fieldWidth;
    return Number.isNaN(originalFieldWidth) ? fieldWidth : originalFieldWidth;
  }

  static scaleFieldHeightToOriginal(fieldAreaHeight, fieldAreaOriginalHeight, fieldHeight) {
    const originalFieldHeight = (fieldAreaOriginalHeight / fieldAreaHeight) * fieldHeight;
    return Number.isNaN(originalFieldHeight) ? fieldHeight : originalFieldHeight;
  }

  static scaleFieldXOriginToOriginal(fieldAreaWidth, fieldAreaOriginalWidth, xOrigin) {
    const originalXOrigin = (fieldAreaOriginalWidth / fieldAreaWidth) * xOrigin;
    return Number.isNaN(originalXOrigin) ? xOrigin : originalXOrigin;
  }

  static scaleFieldYOriginToOriginal(fieldAreaHeight, fieldAreaOriginalHeight, yOrigin) {
    const originalYOrigin = (fieldAreaOriginalHeight / fieldAreaHeight) * yOrigin;
    return Number.isNaN(originalYOrigin) ? yOrigin : originalYOrigin;
  }

  static scaleFontSizeToWebUI(fieldAreaWidth, fieldAreaOriginalWidth, originalFontSize) {
    const fontSize = (fieldAreaWidth / fieldAreaOriginalWidth) * originalFontSize;
    return Number.isNaN(fontSize) ? originalFontSize : fontSize;
  }

  static scaleFieldWidthToWebUI(fieldAreaWidth, fieldAreaOriginalWidth, originalFieldWidth) {
    const fieldWidth = (fieldAreaWidth / fieldAreaOriginalWidth) * originalFieldWidth;
    return Number.isNaN(fieldWidth) ? originalFieldWidth : fieldWidth;
  }

  static scaleFieldHeightToWebUI(fieldAreaHeight, fieldAreaOriginalHeight, originalFieldHeight) {
    const fieldHeight = (fieldAreaHeight / fieldAreaOriginalHeight) * originalFieldHeight;
    return Number.isNaN(fieldHeight) ? originalFieldHeight : fieldHeight;
  }

  static scaleFieldXOriginToWebUI(fieldAreaWidth, fieldAreaOriginalWidth, originalXOrigin) {
    const xOrigin = (fieldAreaWidth / fieldAreaOriginalWidth) * originalXOrigin;
    return Number.isNaN(xOrigin) ? originalXOrigin : xOrigin;
  }

  static scaleFieldYOriginToWebUI(fieldAreaHeight, fieldAreaOriginalHeight, originalYOrigin) {
    const yOrigin = (fieldAreaHeight / fieldAreaOriginalHeight) * originalYOrigin;
    return Number.isNaN(yOrigin) ? originalYOrigin : yOrigin;
  }

  static updateFieldDetails(listOfFields, scale, prevScale) {
    const fields = [];
    for (const field of listOfFields) {
      const tmpField = Object.assign({}, field);
      tmpField.minHeight *= scale / prevScale;
      tmpField.minWidth *= scale / prevScale;
      tmpField.yOrigin *= scale / prevScale;
      tmpField.xOrigin *= scale / prevScale;
      tmpField.fontSize *= scale / prevScale;
      fields.push(tmpField);
    }
    return fields;
  }

  static scaleToOriginal(listOfFields, fieldsArea) {
    const fields = [];
    for (const field of listOfFields) {
      const tmpField = Object.assign({}, field);
      const fieldArea = fieldsArea[`document_${tmpField.documentId}_page_${tmpField.page}`];
      if (fieldArea) {
        const fieldAreaHeight = fieldArea.height;
        const fieldAreaOriginalHeight = fieldArea.originalHeight;
        const fieldAreaWidth = fieldArea.width;
        const fieldAreaOriginalWidth = fieldArea.originalWidth;
        tmpField.minHeight = this.scaleFieldHeightToOriginal(fieldAreaHeight, fieldAreaOriginalHeight, tmpField.minHeight);
        tmpField.minWidth = this.scaleFieldWidthToOriginal(fieldAreaWidth, fieldAreaOriginalWidth, tmpField.minWidth);
        tmpField.yOrigin = this.scaleFieldYOriginToOriginal(fieldAreaHeight, fieldAreaOriginalHeight, tmpField.yOrigin);
        let tmpXOrigin = tmpField.xOrigin;
        if (tmpField.fieldType === fieldType.NAME || tmpField.fieldType === fieldType.DATE) tmpXOrigin = tmpField.xOrigin + 5;
        // else if (tmpField.fieldType === fieldType.FREE_TEXT) tmpXOrigin = tmpField.xOrigin + 10;
        else if (tmpField.fieldType === fieldType.SIGNATURE) tmpXOrigin = tmpField.xOrigin + 5;
        tmpField.xOrigin = this.scaleFieldXOriginToOriginal(fieldAreaWidth, fieldAreaOriginalWidth, tmpXOrigin);
        tmpField.fontSize = this.scaleFontSizeToOriginal(fieldAreaWidth, fieldAreaOriginalWidth, tmpField.fontSize);
      }
      fields.push(tmpField);
    }
    return fields;
  }

  static scaleToWebUI(listOfFields, fieldsArea) {
    const fields = [];
    for (const field of listOfFields) {
      const tmpField = Object.assign({}, field);
      const fieldArea = fieldsArea[`document_${tmpField.documentId}_page_${tmpField.page}`];
      const fieldAreaHeight = fieldArea.height;
      const fieldAreaOriginalHeight = fieldArea.originalHeight;
      const fieldAreaWidth = fieldArea.width;
      const fieldAreaOriginalWidth = fieldArea.originalWidth;
      tmpField.minHeight = this.scaleFieldHeightToWebUI(fieldAreaHeight, fieldAreaOriginalHeight, tmpField.minHeight);
      tmpField.minWidth = this.scaleFieldWidthToWebUI(fieldAreaWidth, fieldAreaOriginalWidth, tmpField.minWidth);
      tmpField.yOrigin = this.scaleFieldYOriginToWebUI(fieldAreaHeight, fieldAreaOriginalHeight, tmpField.yOrigin);
      let tmpXOrigin = this.scaleFieldXOriginToWebUI(fieldAreaWidth, fieldAreaOriginalWidth, tmpField.xOrigin);
      if (tmpField.fieldType === fieldType.NAME || tmpField.fieldType === fieldType.DATE) tmpXOrigin -= 5;
      // else if (tmpField.fieldType === fieldType.FREE_TEXT) tmpXOrigin -= 10;
      else if (tmpField.fieldType === fieldType.SIGNATURE) tmpXOrigin -= 5;
      tmpField.xOrigin = tmpXOrigin;
      tmpField.fontSize = this.scaleFontSizeToWebUI(fieldAreaWidth, fieldAreaOriginalWidth, tmpField.fontSize);

      fields.push(tmpField);
    }
    return fields;
  }
}

export default FieldUtil;
